/**
 * Creates a deep clone of an object or value.
 * This function handles primitive types, arrays, and objects with circular references.
 *
 * @param value The value to clone
 * @returns A deep clone of the provided value
 */
export function cloneDeep<T>(value: T): T {
  // Handle null, undefined, and primitive types
  if (value === null || value === undefined || typeof value !== 'object') {
    return value;
  }

  // Use WeakMap to handle circular references
  const visited = new WeakMap<object, any>();

  function clone<U>(item: U, refs = visited): U {
    // Return primitives directly
    if (item === null || typeof item !== 'object') {
      return item;
    }

    // Handle Date objects
    if (item instanceof Date) {
      return new Date(item.getTime()) as unknown as U;
    }

    // Handle RegExp objects
    if (item instanceof RegExp) {
      return new RegExp(item) as unknown as U;
    }

    // Handle Arrays
    if (Array.isArray(item)) {
      // Check for circular reference
      if (refs.has(item as unknown as object)) {
        return refs.get(item as unknown as object);
      }

      const copy: any[] = [];
      refs.set(item as unknown as object, copy);

      copy.push(...item.map((i) => clone(i, refs)));
      return copy as unknown as U;
    }

    // Handle Objects
    // Check for circular reference
    if (refs.has(item as unknown as object)) {
      return refs.get(item as unknown as object);
    }

    const copy = Object.create(Object.getPrototypeOf(item));
    refs.set(item as unknown as object, copy);

    Object.entries(item as unknown as object).forEach(([key, val]) => {
      copy[key] = clone(val, refs);
    });

    return copy;
  }

  return clone(value);
}
